'use client';
import {
  Banner,
  Button,
  EmptyState,
  IconSticker,
  Table,
  Title,
} from '@yaradigitallabs/ahua-react';
import { getClassNames } from '@/utils/helpers';
import { LoaderPlaceholder } from '@/components/Placeholder';
import type React from 'react';
import type {
  Material,
  MaterialWithSoftTag,
} from '@/features/match-maker/utils/match-maker.types';
import { useMemo, useState } from 'react';
import { getItemTypeFromSelectedItem } from '@/features/match-maker/utils/match-maker.helpers';
import {
  ProductionReportMeasurementEntryEnum,
  type ChangeCalculationParams,
  type ProductionPeriod,
  type ProductionReportMeasurementInput,
} from '../../utils/types';
import CalculationTableCell from './calculation-table-cell';
import { ReportStatus } from '../../enums';
import TagDetailDialog from './tag-detail-dialog';

type RRProductionReportListTableProps = {
  productionPeriod?: ProductionPeriod;
  material: Material | null;
  isLoading: boolean;
  error?: React.ReactNode;
  onChangeCalculation: (params: ChangeCalculationParams) => void;
  measurements: Record<string, ProductionReportMeasurementInput>;
};

const RRProductionReportListTable: React.FC<
  RRProductionReportListTableProps
> = ({
  material,
  isLoading,
  error,
  onChangeCalculation,
  measurements,
  productionPeriod,
}) => {
  const componentList = useMemo(() => {
    return material?.components || [];
  }, [material]);

  const routingList = useMemo(() => {
    return material?.routings || [];
  }, [material]);
  const [tagDetail, selectTagDetail] = useState<{
    tagId: string | null;
    tags: Array<MaterialWithSoftTag>;
  } | null>(null);

  const isViewOnly = productionPeriod?.status !== ReportStatus.REPORT_PENDING;
  return (
    <>
      <div className="border border-gray-300 rounded-lg bg-white flex-1">
        <div className="flex items-center px-8 py-4 border-b border-gray-300">
          <Title maxLineCount={1} size="s">
            Production periods
          </Title>
        </div>
        <div className="px-4 h-[calc(100vh-360px)] md:h-[calc(100vh-360px)] overflow-auto">
          <Table
            className={getClassNames(
              'w-full relative',
              !isLoading && !material ? 'h-full' : ''
            )}
          >
            <thead className="sticky top-0 bg-white z-10 shadow-sm">
              <Table.Row>
                <Table.Head className="py-5! px-3! text-nowrap">
                  Material number
                </Table.Head>
                <Table.Head className="py-5! px-3! text-nowrap">
                  Material description
                </Table.Head>
                <Table.Head className="py-5! px-3! text-nowrap">
                  Type
                </Table.Head>
                <Table.Head className="py-5! px-3! text-nowrap w-[3rem]">
                  Calculation method
                </Table.Head>
                {!isViewOnly && (
                  <Table.Head className="py-5! px-3! max-w-[2rem]"></Table.Head>
                )}
              </Table.Row>
            </thead>
            <tbody>
              {isLoading && (
                <>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <Table.Row className="border-b-0!" key={`loader_${index}`}>
                      <Table.Cell className="py-5! px-3! text-nowrap">
                        <LoaderPlaceholder />
                      </Table.Cell>
                      <Table.Cell className="py-5! px-3! text-nowrap">
                        <LoaderPlaceholder />
                      </Table.Cell>
                      <Table.Cell className="py-5! px-3! text-nowrap">
                        <LoaderPlaceholder />
                      </Table.Cell>
                      <Table.Cell className="py-5! px-3! text-nowrap">
                        <LoaderPlaceholder />
                      </Table.Cell>
                      {!isViewOnly && (
                        <Table.Cell className="py-5! px-3! text-nowrap">
                          <LoaderPlaceholder />
                        </Table.Cell>
                      )}
                    </Table.Row>
                  ))}
                </>
              )}
              {error && (
                <Table.Row className="border-b-0!">
                  <Table.Cell
                    className="text-center py-6"
                    colSpan={isViewOnly ? 4 : 5}
                  >
                    <Banner colorConcept="destructiveLight" open>
                      <Banner.Middle>
                        <Banner.Body>
                          <Banner.Message size="n">{error}</Banner.Message>
                        </Banner.Body>
                      </Banner.Middle>
                    </Banner>
                  </Table.Cell>
                </Table.Row>
              )}
              {!isLoading && !error && !material && (
                <Table.Row className="border-b-0!">
                  <Table.Cell
                    className="text-center py-6"
                    colSpan={isViewOnly ? 4 : 5}
                  >
                    <EmptyState
                      captionText=""
                      iconStickerProps={{
                        icon: 'Info',
                        size: 'xl',
                      }}
                      className="w-xs lg:max-w-sm! mx-auto"
                      titleText="No period added yet"
                    >
                      <EmptyState.Start>
                        <IconSticker icon="Info" size="xl" />
                      </EmptyState.Start>
                      <EmptyState.Middle>
                        <EmptyState.Title>No period added yet</EmptyState.Title>
                        <EmptyState.Caption>
                          Click the button "Add production period" on the top
                          right to start adding the periods.
                        </EmptyState.Caption>
                      </EmptyState.Middle>
                    </EmptyState>
                  </Table.Cell>
                </Table.Row>
              )}
              {!isLoading && !error && material && (
                <>
                  <Table.Row>
                    <Table.Cell
                      colSpan={isViewOnly ? 4 : 5}
                      className="bg-[#F3F6F9] leading-none! py-4! px-3! font-bold"
                    >
                      Production
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell className="py-3! px-3! text-nowrap">
                      {material?.material_code || '-'}
                    </Table.Cell>
                    <Table.Cell className="py-3! px-3! text-nowrap">
                      {material?.description || '-'}
                    </Table.Cell>
                    <Table.Cell className="py-3! px-3! text-nowrap">
                      {getItemTypeFromSelectedItem(material)}
                    </Table.Cell>
                    <Table.Cell className="py-3! px-3! text-nowrap">
                      <CalculationTableCell
                        tags={material.tags || []}
                        measurement={measurements[material.id] || null}
                        productionPeriod={productionPeriod}
                        {...(measurements[material.id]?.entry_type ===
                        ProductionReportMeasurementEntryEnum.TAG
                          ? {
                              onDetail: () =>
                                selectTagDetail({
                                  tagId: measurements[material.id].tag_id,
                                  tags: material.tags || [],
                                }),
                            }
                          : {})}
                      />
                    </Table.Cell>
                    {!isViewOnly && (
                      <Table.Cell className="py-3! px-3! text-right">
                        <Button
                          size="s"
                          variant="ghost"
                          colorConcept="brand"
                          onClick={() =>
                            onChangeCalculation({
                              material,
                            })
                          }
                        >
                          Change
                        </Button>
                      </Table.Cell>
                    )}
                  </Table.Row>
                  <Table.Row>
                    <Table.Cell
                      colSpan={isViewOnly ? 4 : 5}
                      className="bg-[#F3F6F9] leading-none! py-4! px-3! font-bold"
                    >
                      Consumption
                    </Table.Cell>
                  </Table.Row>
                  {componentList.map(component => (
                    <Table.Row key={component.id}>
                      <Table.Cell className="py-3! px-3! text-nowrap">
                        {component.component || '-'}
                      </Table.Cell>
                      <Table.Cell className="py-3! px-3! text-nowrap">
                        {component.description || '-'}
                      </Table.Cell>
                      <Table.Cell className="py-3! px-3! text-nowrap">
                        {getItemTypeFromSelectedItem(component)}
                      </Table.Cell>
                      <Table.Cell className="py-3! px-3! text-nowrap">
                        <CalculationTableCell
                          tags={component.tags || []}
                          measurement={measurements[component.id] || null}
                          productionPeriod={productionPeriod}
                          {...(measurements[component.id]?.entry_type ===
                          ProductionReportMeasurementEntryEnum.TAG
                            ? {
                                onDetail: () =>
                                  selectTagDetail({
                                    tagId: measurements[component.id].tag_id,
                                    tags: component.tags || [],
                                  }),
                              }
                            : {})}
                        />
                      </Table.Cell>
                      {!isViewOnly && (
                        <Table.Cell className="py-3! px-3! text-right">
                          <Button
                            size="s"
                            variant="ghost"
                            colorConcept="brand"
                            onClick={() =>
                              onChangeCalculation({
                                material: component,
                              })
                            }
                          >
                            Change
                          </Button>
                        </Table.Cell>
                      )}
                    </Table.Row>
                  ))}
                  {routingList.map(routing => (
                    <Table.Row key={routing.id}>
                      <Table.Cell className="py-3! px-3! text-nowrap">
                        {routing.workcenter || '-'}
                      </Table.Cell>
                      <Table.Cell className="py-3! px-3! text-nowrap">
                        {routing.object_id || '-'}
                      </Table.Cell>
                      <Table.Cell className="py-3! px-3! text-nowrap">
                        {getItemTypeFromSelectedItem(routing)}
                      </Table.Cell>
                      <Table.Cell className="py-3! px-3! text-nowrap">
                        <CalculationTableCell
                          tags={routing.tags || []}
                          measurement={measurements[routing.id] || null}
                          productionPeriod={productionPeriod}
                          {...(measurements[routing.id]?.entry_type ===
                          ProductionReportMeasurementEntryEnum.TAG
                            ? {
                                onDetail: () =>
                                  selectTagDetail({
                                    tagId: measurements[routing.id].tag_id,
                                    tags: routing.tags || [],
                                  }),
                              }
                            : {})}
                        />
                      </Table.Cell>
                      {!isViewOnly && (
                        <Table.Cell className="py-3! px-3! text-right">
                          <Button
                            size="s"
                            variant="ghost"
                            colorConcept="brand"
                            onClick={() =>
                              onChangeCalculation({
                                material: routing,
                              })
                            }
                          >
                            Change
                          </Button>
                        </Table.Cell>
                      )}
                    </Table.Row>
                  ))}
                </>
              )}
            </tbody>
          </Table>
        </div>
      </div>
      <TagDetailDialog
        tagId={tagDetail?.tagId || null}
        onClose={() => {
          selectTagDetail(null);
        }}
        tags={tagDetail?.tags || []}
      />
    </>
  );
};

export default RRProductionReportListTable;
